import React, { useState, useEffect } from 'react';
import axios from 'axios';
// 1. Added Lock to imports
import { User, MapPin, Calendar, CheckCircle, CreditCard, Briefcase, Star, TrendingUp, Award, X, Edit, Camera, Shield, Phone, Globe, MessageSquare, Bookmark, ListChecks, Clock, Sun, FileText, Moon, Lock } from 'lucide-react';
import { API_BASE_URL } from '../config';
import Avatar from "./ui/Avatar";
import { jwtDecode } from 'jwt-decode';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Badge from "./ui/Badge";
import Button from "./ui/Button";
import Card from "./ui/Card";
import styles from './BusinessProfilePureCSS.module.css';
import PortfolioLightbox from '../search/PortfolioLightbox'; 
import api from '../api/api.js';
import { useNavigate } from 'react-router-dom';
import VerifiedPill from '../components/VerifiedBadge/VerifiedPill.jsx';

const BusinessProfilePureCSS = ({ isOpen, onClose, businessId, useIDTYPE, showMessageButton = true }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRendered, setIsRendered] = useState(false);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);

  const navigate = useNavigate();

  // --- NEW: MASKING LOGIC HELPERS ---
  
  // Navigate to the enquiry page
  const handleEnquiryRedirect = () => {
    // Optional: Close modal before navigating
    // onClose(); 
    navigate(`/contact-business?businessId=${profile.businesses.businessId}`);
  };

  // Mask Address: "****, VIC 3000"
  const getMaskedAddress = (fullAddress) => {
      if (!fullAddress) return 'Location Hidden';
      const postcodeMatch = fullAddress.match(/\b\d{4}\b/); 
      const stateMatch = fullAddress.match(/\b(NSW|VIC|QLD|WA|SA|TAS|NT|ACT)\b/i);
      const state = stateMatch ? stateMatch[0].toUpperCase() : '';
      const postcode = postcodeMatch ? postcodeMatch[0] : '';
      if (state || postcode) return `****, ${state} ${postcode}`;
      return '**** (Address Protected)';
  };

  // Mask Phone: "0412 *** ***"
  const getMaskedPhone = (phone) => {
      if (!phone) return '04** *** ***';
      return `${phone.substring(0, 4)} *** ***`; 
  };


  const checkIfSaved = async () => {
      try {
          const token = localStorage.getItem('token');
          if (!token) return;

          const decodedToken = jwtDecode(token);
          const userType = decodedToken.UserType;
          
          const homeUserId = parseInt(localStorage.getItem('homeUserId'), 10) || 0;
          const bizUserId  = parseInt(localStorage.getItem('businessId'), 10) || 0;

          const params = new URLSearchParams({
              savedBusinessId: profile.businesses.businessId,
              userType,
          });

          if (userType === 'Home') {
              params.append('homeUserId', homeUserId);
          } else {
              params.append('businessId', bizUserId);
          }

          const { data } = await axios.get(
              `${API_BASE_URL}/api/BusinessProfile/isBusinessFavourite?${params.toString()}`,
              { headers: { Authorization: `Bearer ${token}` } }
          );

          if (typeof data.isFavourite === 'boolean') {
              setIsSaved(data.isFavourite);
          }
      } catch (err) {
          console.error('Error checking favourite status', err);
      }
  };

  const handleSaveFavourite = async () => {
      try {
          const token = localStorage.getItem('token');
          if (!token) {
              toast.error("You must be logged in to save a business.");
              return;
          }
          const decodedToken = jwtDecode(token);
          const userType = decodedToken.UserType;

          const loggedInHomeUserId  = parseInt(localStorage.getItem('loggedInHomeUserId'), 10)  || 0;
          const loggedInBusinessId  = parseInt(localStorage.getItem('loggedInBusinessId'), 10)  || 0;

          const payload = {
              savedBusinessId: profile.businesses.businessId,
              homeUserId: userType === 'Home' ? loggedInHomeUserId : 0,
              businessId: userType === 'Business' ? loggedInBusinessId : 0,
              userType,
              commend: ''
          };

          const { data } = await api.post(
              `/api/BusinessProfile/toggleBusinessFavourite`,
              payload,
              { headers: { Authorization: `Bearer ${token}` } }
          );

          if (data.status === 'saved' || data.status === 'removed') {
              const newIsSaved = data.status === 'saved';
              setIsSaved(newIsSaved);
              toast.success(newIsSaved ? 'Added to your favourites' : 'Removed from your favourites');
          } else {
              toast.error('Unexpected response from server.');
          }
      } catch (err) {
          console.error(err);
          toast.error('Could not update favourite status.');
      }
  };

  // Modified to support logic inside render
  const getDirections = (address) => {
    if (!address) {
      toast.error("Address is not available.");
      return;
    }
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
    } else {
      const timer = setTimeout(() => setIsRendered(false), 300); 
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && businessId) {
      const fetchBusinessProfile = async () => {
        setLoading(true);
        setError(null);
        try {
          var url = "";
          if (useIDTYPE == "BID") {
            url = `/api/BusinessProfile/getBusinessProfile?businessId=${businessId}&logView=true`;
          } else {
            url = `/api/BusinessProfile/getBusinessProfileWithBUID?businessUserId=${businessId}`;
          }
          const response = await api.get(url);
          setProfile(response.data);
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      };
      fetchBusinessProfile();
    }
  }, [isOpen, businessId, useIDTYPE]);

  useEffect(() => {
    if (profile) {
      checkIfSaved();
    }
  }, [profile]);

  if (!isRendered) return null;

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`${styles.starIcon} ${i < Math.round(rating) ? styles.starFilled : styles.Empty}`}
      />
    ));
  };

  const handleMessageClickConcise = async (quote) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
            navigate('/login');
            return;
            }
            const { nameid: currentUserId, UserType } = jwtDecode(token);
            const myuserType = localStorage.getItem('userType');
            let url = "";

            const isHome = myuserType === 'Home';

            const senderBusinessUserId = isHome
            ? quote.businessuserid   
            : currentUserId;              
    
            const homeUserId = isHome
            ? currentUserId             
            : undefined;
    
            const receiverBusinessUserId = !isHome
            ? quote.businessuserid 
            : undefined;
    
            if (myuserType == "Home")
            {
                url = `senderBusinessUserId=${senderBusinessUserId}&homeUserId=${homeUserId}`;
            }
            else
            {
                url = `senderBusinessUserId=${senderBusinessUserId}&receiverBusinessUserId=${receiverBusinessUserId}`;                
            }

            const { data } = await api.get(
            `/api/Chat/NewConversation?${url}`
            );
    
            const { conversationId } = data;
            const path = isHome
            ? `/home_messages?conversationId=${conversationId}`
            : `/business_messages?conversationId=${conversationId}`;
    
            window.open(path, '_blank', 'noopener,noreferrer');
        } catch (err) {
            console.error('Error starting conversation:', err);
        }
  };

  const primaryLocation = profile?.locations?.find(loc => loc.isPrimary);
  // Shortcut to verification status
  const isVerified = profile?.businesses?.isVerified;
  const isDocVerified = profile?.businesses?.isDocApproved; 

  return (
    <div className={`${styles.profileModalOverlay} ${isOpen ? styles.overlayVisible : ''}`} onClick={onClose}>
      <div
        className={`${styles.profileModal} ${isOpen ? styles.visible : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className={styles.loadingContainer}>Loading...</div>
        ) : error ? (
          <div className={styles.errorContainer}>Error: {error}</div>
        ) : !profile ? (
          <div className={styles.errorContainer}>No profile data found.</div>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={onClose} className={styles.profileCloseBtn}>
                <X className="w-5 h-5" />
            </Button>

            {/* --- COVER IMAGE BANNER --- */}
            <div className={styles.coverImageBanner}>
                {profile.businesses.coverImage ? (
                    <img
                        src={profile.businesses.coverImage}
                        alt=""
                        className={styles.coverImageImg}
                    />
                ) : (
                    <div className={styles.coverImagePlaceholder} />
                )}
                <div className={styles.coverImageGradient} />
            </div>

            <div className={styles.profileHeader}>
                <div className={styles.headerContent}>
                    {/* --- LEFT COLUMN: IDENTITY & TRUST --- */}
                    <div className={styles.identity}>
                        <Avatar
                            className={styles.profileLogo}
                            src={profile.businesses.profilePic || "/placeholder.svg"}
                            fallback={profile.businesses.businessName.charAt(0)}
                        />
                        <div className={styles.identityText}>
                            <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap: 'wrap'}}>
                                <h1 style={{ margin: 0 }}>{profile.businesses.businessName}</h1>

                                {isVerified && isDocVerified ? (
                                    <VerifiedPill />
                                ) : isVerified && !isDocVerified ? (
                                    <Badge variant="secondary" style={{fontSize:'11px', background:'#fef3c7', color:'#d97706', padding: '4px 8px'}}>
                                        Pending Verification
                                    </Badge>
                                ) : (
                                    <Badge variant="secondary" style={{fontSize:'11px', background:'#f1f5f9', color:'#64748b', padding: '4px 8px'}}>
                                        Unclaimed
                                    </Badge>
                                )}
                            </div>
                            <p className={styles.tagline}>{profile.businesses.description}</p>

                            {profile.statistics.reviewCount > 0 ? (
                                <div className={styles.ratingInfo} onClick={() => setActiveTab("reviews")} style={{ cursor: 'pointer' }}>
                                    <div className={styles.starsContainer}>{renderStars(profile.statistics.averageRating)}</div>
                                    <strong>{profile.statistics.averageRating?.toFixed(1)}</strong>
                                    <span>({profile.statistics.reviewCount} {profile.statistics.reviewCount === 1 ? 'Build Start Review' : 'Build Start Reviews'})</span>
                                </div>
                            ) : (
                                <div className={styles.ratingInfo}>
                                    <Badge variant="secondary" style={{fontSize:'12px', background:'#eff6ff', color:'#2563eb', fontWeight: 600, padding:'4px 10px', borderRadius:'12px'}}>
                                        New Pro — No reviews yet
                                    </Badge>
                                </div>
                            )}
                            {profile.statistics.googleRating && profile.statistics.googleRatingCount > 0 && (
                                <div className={`${styles.ratingInfo} ${styles.googleRatingInfo}`}>
                                    <Globe size={14} className={styles.googleIcon}/>
                                    <strong>Google Rating: {profile.statistics.googleRating.toFixed(1)}</strong>
                                    <span>({profile.statistics.googleRatingCount} Reviews)</span>
                                </div>
                            )}
                            
                            <div className={styles.badgeContainer}>
                                {profile.businesses.fullyInsured && <div className={styles.badge}><Shield size={14}/> Fully Insured</div>}
                                {profile.businesses.yearsExperience > 0 && <div className={styles.badge}><Award size={14}/> {profile.businesses.yearsExperience} {profile.businesses.yearsExperience === 1 ? 'Year' : 'Years'} Experience</div>}
                                {profile.businesses.afterHours !== 0 && <div className={styles.badge}><Clock size={14}/> After Hours</div>}
                                {profile.businesses.weekends !== 0 && <div className={styles.badge}><Sun size={14}/> Weekends</div>}
                            </div>
                        </div>
                    </div>

                    {/* --- RIGHT COLUMN: ACTIONS --- */}
                    <div className={styles.actions}>
                        {/* Primary CTA */}
                        {isVerified ? (
                            <a href={`tel:${profile.businesses.phone}`} className={styles.primaryCta} style={{textDecoration:'none', display:'flex', alignItems:'center', gap:'8px'}}>
                                <Phone size={16} /> Contact Business
                            </a>
                        ) : (
                            <Button className={styles.primaryCta} onClick={handleEnquiryRedirect} style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                <MessageSquare size={16} /> Request Quote
                            </Button>
                        )}

                        {/* Phone number shown below CTA */}
                        <div style={{fontSize:'0.85rem', color:'#64748b', textAlign:'right', display:'flex', alignItems:'center', gap:'6px'}}>
                            <Phone size={13} />
                            {isVerified ? (
                                <a href={`tel:${profile.businesses.phone}`} style={{color:'#334155', textDecoration:'none', fontWeight:500}}>
                                    {profile.businesses.phone}
                                </a>
                            ) : (
                                <span onClick={handleEnquiryRedirect} style={{cursor:'pointer', fontFamily:'monospace'}}>
                                    {getMaskedPhone(profile.businesses.phone)} <Lock size={11} style={{verticalAlign:'-1px'}} />
                                </span>
                            )}
                        </div>

                        <div className={styles.secondaryActions}>
                            {showMessageButton && (
                                <Button className={styles.iconBtn} onClick={() => handleMessageClickConcise({businessuserid: profile.businessUserId})}>
                                    <MessageSquare size={14} /> Message
                                </Button>
                            )}

                            <Button className={styles.iconBtn} onClick={handleSaveFavourite}>
                                <Bookmark size={14} fill={isSaved ? 'currentColor' : 'none'} /> {isSaved ? 'Saved' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.profileModalContent}>
                <div className={styles.profileTabs}>
                    <div className={styles.profileTabsList}>
                        <button className={`${styles.profileTab} ${activeTab === "overview" ? styles.profileTabActive : ""}`} onClick={() => setActiveTab("overview")}>Overview</button>
                        <button className={`${styles.profileTab} ${activeTab === "reviews" ? styles.profileTabActive : ""}`} onClick={() => setActiveTab("reviews")}>Reviews ({profile.customerReviews?.length || 0})</button>
                        <button className={`${styles.profileTab} ${activeTab === "portfolio" ? styles.profileTabActive : ""}`} onClick={() => setActiveTab("portfolio")}>Work Portfolio</button>
                    </div>
                  </div>

                <div className={styles.profileTabsContent}>
                    {activeTab === "overview" && (
                        <div className={styles.overviewGrid}>
                            <div className={styles.overviewMain}>
                                <div className={styles.section}>
                                    <h3 className={styles.sectionTitle}>About {profile.businesses.businessName}</h3>
                                    <p>{profile.businesses.profileDescription || "No detailed description provided."}</p>
                                </div>
                                <div className={styles.section}>
                                    <h3 className={styles.sectionTitle}><ListChecks /> Services Offered</h3>
                                    {(() => {
                                        const allServices = [
                                            ...(profile.professions?.map(p => ({ key: `p-${p.professionId}`, name: p.name })) || []),
                                            ...(profile.services?.map(s => ({ key: `s-${s.serviceId}`, name: s.name })) || []),
                                        ];
                                        const visibleServices = showAllServices ? allServices : allServices.slice(0, 8);
                                        const hiddenCount = allServices.length - 8;
                                        return (
                                            <div className={styles.tagContainer}>
                                                {visibleServices.map(item => <span key={item.key} className={styles.tag}>{item.name}</span>)}
                                                {!showAllServices && hiddenCount > 0 && (
                                                    <span
                                                        className={styles.tag}
                                                        onClick={() => setShowAllServices(true)}
                                                        style={{cursor:'pointer', background:'#dbeafe', color:'#2563eb', fontWeight:600}}
                                                    >
                                                        +{hiddenCount} more
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                                 <div className={styles.section}>
                                    <h3 className={styles.sectionTitle}><Award /> Certifications & Qualifications</h3>
                                    <div className={styles.tagContainer}>
                                        {profile.certifications?.map(cert => <span key={cert.certificationId} className={styles.tag}>{cert.name}</span>)}
                                        {profile.qualificationDtos?.map(qual => <span key={qual.qualificationId} className={styles.tag}>{qual.name}</span>)}
                                    </div>
                                </div>
                            </div>
                            
                            {/* --- RIGHT COLUMN SIDEBAR (UPDATED WITH TRAP) --- */}
                            <div className={styles.overviewSidebar}>
                                <div className={styles.sidebarCard}>
                                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
                                        <h4 className={styles.cardTitle} style={{margin:0}}><MapPin /> Location</h4>
                                        {!isVerified && (
                                            <span style={{fontSize:'0.75rem', color:'#64748b', background:'#f1f5f9', padding:'2px 6px', borderRadius:'4px', display:'flex', alignItems:'center', gap:'4px'}}>
                                                <Lock size={10}/> Privacy
                                            </span>
                                        )}
                                    </div>

                                    {primaryLocation ? (
                                        <div className={styles.locationSection}>
                                            {/* Masked/Unmasked Address */}
                                            <p 
                                                onClick={!isVerified ? handleEnquiryRedirect : undefined}
                                                style={!isVerified ? {cursor:'pointer', fontFamily:'monospace', color:'#64748b'} : {}}
                                            >
                                                {isVerified ? primaryLocation.address : getMaskedAddress(primaryLocation.address)}
                                            </p>

                                            {/* Masked/Unmasked Phone */}
                                            {(primaryLocation.mobile || primaryLocation.landline) && (
                                                <div 
                                                    className={styles.locationPhone}
                                                    onClick={!isVerified ? handleEnquiryRedirect : undefined}
                                                    style={!isVerified ? {cursor:'pointer'} : {}}
                                                >
                                                    <Phone size={14}/> 
                                                    {isVerified 
                                                        ? (primaryLocation.mobile || primaryLocation.landline)
                                                        : getMaskedPhone(primaryLocation.mobile || primaryLocation.landline)
                                                    }
                                                </div>
                                            )}
                                            
                                            {/* Trapped Directions Button */}
                                            <button 
                                                className={styles.directionsButton} 
                                                onClick={() => isVerified ? getDirections(primaryLocation.address) : handleEnquiryRedirect()}
                                            >
                                                {isVerified ? "Get Directions" : "Request Location"}
                                            </button>
                                        </div>
                                    ) : <p>No primary location set.</p>}
                                    
                                    {/* Other Locations List */}
                                    {profile.locations?.filter(loc => !loc.isPrimary).length > 0 && (
                                        <div className={styles.otherLocations}>
                                            <h5>Other locations</h5>
                                            <ul>
                                                {profile.locations?.filter(loc => !loc.isPrimary).map(location => (
                                                    <li key={location.locationId} className={styles.locationItem}>
                                                        <div className={styles.locationDetails}>
                                                            <p onClick={!isVerified ? handleEnquiryRedirect : undefined} style={!isVerified ? {cursor:'pointer'} : {}}>
                                                                {isVerified ? location.address : getMaskedAddress(location.address)}
                                                            </p>
                                                        </div>
                                                        <button 
                                                            className={styles.directionsButtonSecondary} 
                                                            onClick={() => isVerified ? getDirections(location.address) : handleEnquiryRedirect()}
                                                        >
                                                            {isVerified ? "Directions" : "Req. Map"}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* ONLY SHOW IF FULLY VERIFIED */}
                                {isVerified && isDocVerified && (
                                    <div className={styles.sidebarCard} style={{borderColor: '#bbf7d0', borderWidth:'2px'}}>
                                        <h4 className={styles.cardTitle} style={{background:'linear-gradient(135deg, #34d399, #059669)', color:'white', margin:'-20px -20px 16px -20px', padding:'12px 20px', borderRadius:'6px 6px 0 0', fontSize:'15px'}}>
                                            <CheckCircle size={16} /> Verified Credentials
                                        </h4>
                                        <div className={styles.verificationBox}>
                                            {profile.businesses.abn && <p><strong>ABN:</strong> {profile.businesses.abn}</p>}
                                            {profile.businesses.licenseNumber && <p><strong>License No:</strong> {profile.businesses.licenseNumber}</p>}
                                            {profile.businesses.registrationNumber && <p><strong>Registration No:</strong> {profile.businesses.registrationNumber}</p>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === "reviews" && (
                      <div className={styles.profileReviews}>
                        <div className={styles.reviewsList}>
                          {profile.customerReviews && profile.customerReviews.length > 0 ? (
                            profile.customerReviews.map((review) => (
                              <Card key={review.reviewId} className={styles.reviewCard}>
                                <div className={styles.reviewContent}>
                                  <div className={styles.reviewHeader}>
                                    <div className={styles.reviewBusiness}>
                                      <Avatar
                                        className={styles.reviewAvatar}
                                        src={review.userProfileImage || "/placeholder.svg"}
                                        fallback={review.userName.charAt(0)}
                                      />
                                      <div className={styles.reviewBusinessInfo}>
                                        <div className={styles.reviewBusinessName}>
                                          {review.userName}
                                        </div>
                                      </div>
                                    </div>
                                    <div className={styles.reviewMeta}>
                                      <div className={styles.reviewRating}>{renderStars(review.overallRating)}</div>
                                    </div>
                                  </div>
                                  <div className={styles.reviewComment}>"{review.description}"</div>
                                </div>
                              </Card>
                            ))
                          ) : (
                            <p>No reviews available.</p>
                          )}
                        </div>
                      </div>
                    )}

                    {activeTab === "portfolio" && (
                      <div className={styles.portfolioSection}>
                          {profile.workPortfolio && profile.workPortfolio.model ? (
                              profile.workPortfolio.model.map(item => (
                                  <div key={item.portfolioId} className={styles.portfolioCard} onClick={() => setSelectedPortfolioItem(item)}>
                                      <img src={item.mediaUrl?.[0]?.url} alt={item.title} className={styles.portfolioImage} />
                                      <div className={styles.portfolioCardContent}>
                                          <h4 className={styles.portfolioTitle}>{item.title}</h4>
                                          <p className={styles.portfolioDescription}>{item.description}</p>
                                      </div>
                                  </div>
                              ))
                          ) : (
                              <p>No portfolio items available.</p>
                          )}
                      </div>
                    )}
                </div>
            </div>
          </>
        )}
      </div>

      {selectedPortfolioItem && (
          <PortfolioLightbox
              portfolioItem={selectedPortfolioItem}
              onClose={() => setSelectedPortfolioItem(null)}
          />
      )}
    </div>
  );
};

export default BusinessProfilePureCSS;